import React, { useEffect, useState } from 'react';
import styles from './styles.module.css';
import Text from 'components/shared/Text';
import history from 'store/history';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';

const MembershipForms = ({
  loadFreezeDropdownData,
  loadCancelDropdownData,
  freezeDropdownDetails,
  cancelDropdownDetails,
  isCancelIframeLoading,
  cancelIframeDetails,
  isFreezeIframeLoading,
  freezeIframeDetails,
  loadCancelIframe,
  loadFreezeIframe,
  loadFormsDetails,
  formsDetails,
  isFormsDetailsLoading,
  resetIframeDetails,
  setError,
}) => {
  const userId = useSelector(state => state?.auth?.profile?.userID);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingType, setProcessingType] = useState(null); // 'cancel' or 'freeze'

  useEffect(() => {
    if (Object.keys(freezeDropdownDetails).length === 0) {
      loadFreezeDropdownData();
    }
    if (Object.keys(cancelDropdownDetails).length === 0) {
      loadCancelDropdownData();
    }
  }, []);

  // Reset iframe details and load forms details when component mounts or userId changes to clear stale data from previous sessions
  useEffect(() => {
    resetIframeDetails();
    if (userId) {
      loadFormsDetails(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (isProcessing && processingType === 'cancel' && !isCancelIframeLoading) {
      if (cancelIframeDetails?.success) {
        const navigationUrl = {
          url: cancelIframeDetails.data.securedUrl,
        };
        history.push({ pathname: '/membership-form/cancel', state: navigationUrl });
        setIsProcessing(false);
        setProcessingType(null);
      } else if (cancelIframeDetails?.status === 409) {
        setError(cancelIframeDetails?.message);
        setIsProcessing(false);
        setProcessingType(null);
      } else if (
        Object.keys(cancelIframeDetails).length !== 0 &&
        !cancelIframeDetails?.success
      ) {
        history.push('/membership-form/cancel');
        setIsProcessing(false);
        setProcessingType(null);
      }
    }

    if (isProcessing && processingType === 'freeze' && !isFreezeIframeLoading) {
      if (freezeIframeDetails?.success) {
        const navigationUrl = {
          url: freezeIframeDetails.data.securedUrl,
        };
        history.push({ pathname: '/membership-form/freeze', state: navigationUrl });
        setIsProcessing(false);
        setProcessingType(null);
      } else if (freezeIframeDetails?.status === 409) {
        setError(freezeIframeDetails?.message);
        setIsProcessing(false);
        setProcessingType(null);
      } else if (
        Object.keys(freezeIframeDetails).length !== 0 &&
        !freezeIframeDetails?.success
      ) {
        history.push('/membership-form/freeze');
        setIsProcessing(false);
        setProcessingType(null);
      }
    }
  }, [
    isCancelIframeLoading,
    cancelIframeDetails,
    isFreezeIframeLoading,
    freezeIframeDetails,
    isProcessing,
    processingType,
  ]);

  const formTypes = [
    {
      title: 'Freeze Membership',
      subTitle: 'Need a break? Submit your freeze request here.',
      link: '/membership-form/freeze',
    },
    {
      title: 'Cancel Membership',
      subTitle: 'Ready to move on? Submit your cancellation request here.',
      link: '/membership-form/cancel',
    },
  ];

  const handleCancelClick = () => {
    if (userId && !isProcessing) {
      setIsProcessing(true);
      setProcessingType('cancel');
      loadCancelIframe(userId);
    }
  };

  const handleFreezeClick = () => {
    if (userId && !isProcessing) {
      setIsProcessing(true);
      setProcessingType('freeze');
      loadFreezeIframe(userId);
    }
  };

  const goToLink = link => {
    if (isProcessing) return;
    const cancelForm = formsDetails.find(form =>
      form?.name?.toLowerCase().includes('cancel')
    );
    const freezeForm = formsDetails.find(form =>
      form?.name?.toLowerCase().includes('freeze')
    );
    const isCancelEnabled = cancelForm && cancelForm.c2SEnable;
    const isFreezeEnabled = freezeForm && freezeForm.c2SEnable;

    if (link === '/membership-form/cancel' && isCancelEnabled) {
      handleCancelClick();
    } else if (link === '/membership-form/freeze' && isFreezeEnabled) {
      handleFreezeClick();
    } else {
      history.push(link);
    }
  };

  return (
    <>
      <div className={styles.body}>
        <Text className={styles.heading}>Manage Membership</Text>
        <div className={styles.innerContainer}>
          {formTypes.map((data, index) => {
            const isCancelCard = data.link === '/membership-form/cancel';
            const isLoading =
              isFormsDetailsLoading ||
              Object.keys(formsDetails).length === 0 ||
              (isCancelCard && !userId);

            return (
              <div
                className={`${styles.card} ${isLoading ? styles.loadingCard : ''}`}
                key={index}
                onClick={() => !isLoading && goToLink(data.link)}
                style={{
                  position: 'relative',
                  cursor: isLoading ? 'default' : 'pointer',
                }}
              >
                <div className={styles.details}>
                  <p className={styles.key}>{data.title}</p>
                  <p className={styles.value}>{data.subTitle}</p>
                </div>
                {isLoading && (
                  <div className={styles.cardLoader}>
                    <CircularProgress size={24} style={{ color: '#fe5000' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {(isCancelIframeLoading || isFreezeIframeLoading || isProcessing) && (
        <div className={styles.fullPageLoader}>
          <CircularProgress size={60} style={{ color: '#fe5000' }} />
        </div>
      )}
    </>
  );
};

export default MembershipForms;
